import axios from "axios";
import React, { useRef, useState, useEffect, useCallback } from "react";
import { useInfiniteQuery } from "react-query";
import Song from "./Song";
import RecentFile from "./RecentFile";
import { CgClose } from "react-icons/cg";
import { Input } from "@jammable/ui-components/Forms";
import NotFound from "../NotFound";
import { useAuth } from "@/context/AuthProvider";
import { getAuth } from "firebase/auth";
import Tabs from "../Tabs";
import { IoLibrarySharp } from "react-icons/io5";
import { FaUser } from "react-icons/fa";
import { Spinner } from "@jammable/ui-components/Loader";
import { Button } from "@jammable/ui-core";

// Define limit constants
const OFFICIAL_LIMIT = 20;
const USER_LIMIT = 10; // Adjusted limit for user files

// Define hardcoded genres
const HARDCODED_GENRES = [
  { id: "all", name: "🎵 All" },
  { id: "rap", name: "🎤 Rap" },
  { id: "pop", name: "🌟 Pop" },
  { id: "latin", name: "💃 Latin" },
  { id: "clip", name: "🎬 Clips" },
];

const ConversionSongLibrary = ({
  mode,
  onSourceSelected,
  onGetInTouchClick,
}) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("official");
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [genres] = useState(HARDCODED_GENRES);
  const [selectedGenre, setSelectedGenre] = useState("all"); // Default to "all"

  const officialListRef = useRef(null);
  const userListRef = useRef(null);

  // --- Fetch Official Library Songs ---
  const fetchOfficialSongs = async ({ pageParam = 0 }) => {
    const response = await axios.get("/api/library/getLibrarySongs", {
      params: {
        page: pageParam,
        limit: OFFICIAL_LIMIT,
        genre: selectedGenre === "all" ? undefined : selectedGenre, // Pass selected genre
        search: debouncedSearchQuery || undefined,
      },
    });

    return response.data;
  };

  const {
    data: officialData,
    error: officialError,
    fetchNextPage: fetchNextOfficialPage,
    hasNextPage: hasNextOfficialPage,
    isFetching: isFetchingOfficial,
    isFetchingNextPage: isFetchingNextOfficialPage,
    status: officialStatus,
    refetch: refetchOfficialSongs, // Added refetch for official songs
  } = useInfiniteQuery(
    ["officialLibrarySongs", selectedGenre, debouncedSearchQuery], // Add selectedGenre to query key
    fetchOfficialSongs,
    {
      getNextPageParam: (lastPage, pages) => {
        const loadedSongs = pages.reduce(
          (acc, page) => acc + (page?.songs?.length || 0),
          0,
        );
        const totalSongs = lastPage?.total ?? Infinity;
        return loadedSongs < totalSongs &&
          (lastPage?.songs?.length || 0) === OFFICIAL_LIMIT
          ? pages.length
          : undefined;
      },
      enabled: activeTab === "official",
      refetchOnWindowFocus: false,
    },
  );

  // Refetch official songs when selected genre changes
  useEffect(() => {
    if (activeTab === "official") {
      officialListRef.current?.scrollTo(0, 0); // Scroll to top
      refetchOfficialSongs();
    }
  }, [selectedGenre, debouncedSearchQuery, activeTab, refetchOfficialSongs]);

  // --- Fetch User's Recent Files ---
  const fetchUserFiles = async ({ pageParam = 0 }) => {
    if (!user?.firebaseUid) return { files: [], length: 0 };
    const auth = getAuth();
    const token = await auth.currentUser.getIdToken();
    const res = await axios.get(`/api/files/getRecentUploadedFiles`, {
      headers: { Authorization: token },
      params: {
        page: pageParam,
        limit: USER_LIMIT,
        search: debouncedSearchQuery || undefined,
      },
    });
    return res.data;
  };

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const {
    data: userData,
    error: userError,
    fetchNextPage: fetchNextUserPage,
    hasNextPage: hasNextUserPage,
    isFetching: isFetchingUser,
    isFetchingNextPage: isFetchingNextUserPage,
    status: userStatus,
    refetch: refetchUserFiles,
  } = useInfiniteQuery(
    ["userRecentFiles", user?.firebaseUid, debouncedSearchQuery],
    fetchUserFiles,
    {
      getNextPageParam: (lastPage, allPages) => {
        const loadedFiles = allPages.reduce(
          (acc, page) => acc + (page?.files?.length || 0),
          0,
        );
        const totalFiles = lastPage?.length ?? 0;
        return loadedFiles < totalFiles &&
          (lastPage?.files?.length || 0) === USER_LIMIT
          ? allPages.length
          : undefined;
      },
      enabled: !!user?.firebaseUid && activeTab === "user",
      refetchOnWindowFocus: false,
    },
  );

  // Refetch user files when search query changes
  useEffect(() => {
    if (activeTab === "user") {
      refetchUserFiles();
    }
  }, [debouncedSearchQuery, activeTab, refetchUserFiles]);

  // --- Infinite Scroll Logic ---
  const setupScrollListener = useCallback(
    (ref, hasNextPage, fetchNextPage, isFetchingNextPage) => {
      const handleScroll = () => {
        if (!ref.current) return;
        const { scrollTop, scrollHeight, clientHeight } = ref.current;
        if (
          scrollHeight - scrollTop <= clientHeight + 100 &&
          !isFetchingNextPage &&
          hasNextPage
        ) {
          console.log("Fetching next page...");
          fetchNextPage();
        }
      };

      const element = ref.current;
      if (element) {
        element.addEventListener("scroll", handleScroll);
      }
      return () => {
        if (element) {
          element.removeEventListener("scroll", handleScroll);
        }
      };
    },
    [],
  );

  useEffect(() => {
    if (activeTab === "official") {
      return setupScrollListener(
        officialListRef,
        hasNextOfficialPage,
        fetchNextOfficialPage,
        isFetchingNextOfficialPage,
      );
    }
  }, [
    activeTab,
    officialListRef,
    hasNextOfficialPage,
    fetchNextOfficialPage,
    isFetchingNextOfficialPage,
    setupScrollListener,
  ]);

  useEffect(() => {
    if (activeTab === "user") {
      return setupScrollListener(
        userListRef,
        hasNextUserPage,
        fetchNextUserPage,
        isFetchingNextUserPage,
      );
    }
  }, [
    activeTab,
    userListRef,
    hasNextUserPage,
    fetchNextUserPage,
    isFetchingNextUserPage,
    setupScrollListener,
  ]);

  // --- Event Handlers ---
  const handlePlayPause = (itemId) => {
    setCurrentlyPlaying((prev) => (prev === itemId ? null : itemId));
  };

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  // --- Selection Handler ---
  const handleSelect = (itemDetails) => {
    if (onSourceSelected) {
      onSourceSelected(itemDetails);
    } else {
      console.warn("onSourceSelected prop is missing in ConversionSongLibrary");
    }
  };

  const handleSelectGenre = (genreId) => {
    setSelectedGenre(genreId);
  };

  // --- UI Rendering ---
  const LoadingSkeleton = ({ count = 4 }) => (
    <div className="tw-animate-pulse tw-space-y-4">
      {[...Array(count)].map((_, index) => (
        <div key={index} className="tw-flex tw-items-center tw-space-x-4">
          <div className="tw-w-10 tw-h-10 tw-bg-gray-100 tw-rounded-full"></div>
          <div className="tw-w-16 tw-h-16 tw-bg-gray-100 tw-rounded-lg"></div>
          <div className="tw-flex-1 tw-space-y-2">
            <div className="tw-h-4 tw-bg-gray-100 tw-rounded-full tw-w-3/4"></div>
            <div className="tw-h-4 tw-bg-gray-100 tw-rounded-full tw-w-1/2"></div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderOfficialTabContent = () => {
    if (officialStatus === "error") {
      return <p>Error loading official library: {officialError.message}</p>;
    }

    return (
      <>
        {/* Genre Badges */}

        {activeTab === "official" && (
          <div className="tw-mb-4 tw-flex tw-flex-wrap tw-gap-2 tw-grid tw-grid-cols-5">
            {genres.map((genre) => (
              <Button
                key={genre.id}
                onClick={() => handleSelectGenre(genre.id)}
                className={`tw-px-3 tw-text-xs tw-uppercase tw-h-8 tw-w-full tw-py-1  tw-font-black tw-rounded-full  tw-border-none
                  ${
                    selectedGenre === genre.id
                      ? mode === "dark"
                        ? "tw-bg-purple-700 tw-text-white tw-border-purple-500"
                        : "tw-bg-purple-100 tw-text-black hover:tw-bg-purple-200 tw-border-none"
                      : mode === "dark"
                        ? "tw-bg-gray-700 tw-text-gray-300 tw-border-gray-600 hover:tw-bg-gray-600"
                        : "tw-bg-gray-200 tw-text-gray-700 tw-border-gray-300 hover:tw-bg-gray-300"
                  }
                `}
              >
                {genre.name}
              </Button>
            ))}
          </div>
        )}

        <div
          ref={officialListRef}
          className="tw-flex tw-flex-col tw-gap-2 tw-overflow-y-auto tw-max-h-[330px]" // Adjusted max-h to accommodate genres
        >
          {officialStatus === "loading" && !officialData?.pages?.length && (
            <LoadingSkeleton count={OFFICIAL_LIMIT / 5} />
          )}
          {officialData?.pages?.map((page, pageIndex) => (
            <React.Fragment key={pageIndex}>
              {page?.songs?.map((song) => {
                const isClip = song?.metadata?.tags?.includes("clip");
                return (
                  <Song
                    key={song?._id}
                    song={song}
                    mode={mode}
                    onSelect={handleSelect}
                    isPlaying={currentlyPlaying === song?._id}
                    onPlayPause={() => handlePlayPause(song?._id)}
                    isClip={isClip}
                  />
                );
              })}
            </React.Fragment>
          ))}
          {isFetchingNextOfficialPage && (
            <div className="tw-flex tw-justify-center tw-py-4">
              <Spinner size={5} />
            </div>
          )}

          {!isFetchingOfficial &&
            officialData?.pages?.flatMap((p) => p.songs || []).length === 0 && (
              <NotFound
                title={
                  debouncedSearchQuery
                    ? "No Sounds Found"
                    : selectedGenre && selectedGenre !== "all"
                      ? "No Sounds in Genre"
                      : "Library Empty"
                }
              />
            )}
        </div>
      </>
    );
  };

  const renderUserTabContent = () => {
    if (userStatus === "loading" && !userData?.pages?.length) {
      return <LoadingSkeleton count={Math.ceil(USER_LIMIT / 3)} />;
    }
    if (userStatus === "error") {
      return <p>Error loading your files: {userError.message}</p>;
    }

    const userFilesRaw =
      userData?.pages?.flatMap((page) => page?.files || []) || [];

    // De-duplicate based on _id
    const uniqueUserFiles = userFilesRaw.reduce((acc, current) => {
      if (!current?._id) return acc; // Skip if file or _id is missing
      const x = acc.find((item) => item._id === current._id);
      if (!x) {
        return acc.concat([current]);
      } else {
        // Optional: Decide if you want to keep the first or last instance if metadata differs
        return acc;
      }
    }, []);

    return (
      <div
        ref={userListRef}
        className="tw-flex tw-flex-col tw-gap-2 tw-overflow-y-auto tw-max-h-[375px]"
      >
        {uniqueUserFiles.length === 0 && !isFetchingUser ? (
          <div className="tw-w-full tw-gap-5 tw-flex tw-justify-center tw-items-center tw-flex-col tw-text-center tw-font-bold tw-text-xl tw-p-10">
            {debouncedSearchQuery ? (
              <NotFound
                title="No files found"
                description="Try another search term!"
              />
            ) : (
              <NotFound
                title="No recent files"
                description="Upload some audio to see it here!"
              />
            )}
          </div>
        ) : (
          uniqueUserFiles.map((file) => (
            <RecentFile
              key={file._id}
              file={file}
              mode={mode}
              onSelect={handleSelect}
              isPlaying={currentlyPlaying === file._id}
              onPlayPause={() => handlePlayPause(file._id)}
            />
          ))
        )}
        {isFetchingNextUserPage && (
          <div className="tw-flex tw-justify-center tw-py-4">
            <Spinner size={5} />
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`tw-px-0 tw-w-full tw-flex tw-flex-col tw-gap-3 ${mode === "dark" ? "md:tw-px-8" : "md:tw-px-0"}`}
    >
      {/* Header Section (Optional Back Button) */}
      {/* Example: <button onClick={onBack}>Back</button> */}

      {/* Tabs and Search Bar */}
      <div className="tw-flex tw-items-center tw-gap-4">
        <div className="tw-flex-none">
          <Tabs
            tabs={[
              {
                id: "official",
                label: (
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <IoLibrarySharp />
                    Library
                  </div>
                ),
              },
              {
                id: "user",
                label: (
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <FaUser />
                    Recent
                  </div>
                ),
              },
            ]}
            selectedTab={activeTab}
            onTabChange={setActiveTab}
            className={`${mode === "dark" ? "tw-bg-gray-700 tw-text-white" : "tw-text-black"}`}
          />
        </div>
        <div
          className={`tw-relative tw-flex-grow tw-flex tw-items-center ${
            mode === "dark" ? "tw-text-white" : "tw-text-black"
          }`}
        >
          <Input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search..."
            className={`tw-h-10 tw-rounded-full tw-text-sm tw-w-full tw-pl-4 tw-pr-6 ${
              mode === "dark"
                ? "tw-bg-purple-700/30 tw-border-purple-500/50 focus:tw-border-purple-500 tw-text-white placeholder-white/70"
                : "tw-bg-gray-100 tw-border-gray-200 focus:tw-border-gray-300 placeholder-gray-500"
            } tw-border tw-outline-none focus:tw-ring-1 ${
              mode === "dark"
                ? "focus:tw-ring-purple-500"
                : "focus:tw-ring-gray-300"
            }`}
          />
          {searchQuery && (
            <div
              onClick={handleClearSearch}
              className="tw-absolute tw-right-3 tw-cursor-pointer tw-flex tw-items-center tw-justify-center "
              aria-label="Clear search"
            >
              <CgClose
                size={18}
                className={`${
                  mode === "dark" ? "tw-text-white/70" : "tw-text-black"
                }`}
              />
            </div>
          )}
        </div>
      </div>

      {/* Tab Content */}
      <div className="tw-flex-grow tw-overflow-hidden">
        {activeTab === "official" && renderOfficialTabContent()}
        {activeTab === "user" && renderUserTabContent()}
      </div>

      {/* Footer Section (e.g., Contact Link) - Adjusted position */}
      {activeTab === "official" && onGetInTouchClick && (
        <div
          className={`tw-text-sm tw-font-bold ${mode === "dark" ? "tw-text-white/60" : "tw-text-black/40"} tw-text-center `}
        >
          Want to see your sounds here?{" "}
          <span
            onClick={() => {
              window.open("mailto:team@jammable.com", "_blank");
            }}
            className={`${mode === "dark" ? "tw-text-white" : "tw-text-purple-700"} tw-font-black tw-underline ${onGetInTouchClick ? "tw-cursor-pointer" : ""}`}
          >
            Contact Us
          </span>
        </div>
      )}
    </div>
  );
};

export default ConversionSongLibrary;
